import { Component, computed, inject, input, numberAttribute, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta } from '@angular/platform-browser';

import { combineLatest, switchMap, tap } from 'rxjs';

import { SpinnerComponent } from '@components/spinner';
import { NotFoundPageComponent, handleSsrPageErrors } from '@features/errors';

import { CustomPagesService } from '../../services';

@Component({
  selector: 'app-blog-listing',
  templateUrl: './blog-listing.component.html',
  imports: [CommonModule, RouterModule, SpinnerComponent, NotFoundPageComponent],
})
export class BlogListingComponent {
  #service = inject(CustomPagesService);
  #meta = inject(Meta);

  /** 'blog' or 'news' — set by the route data */
  section = input.required<'blog' | 'news'>();

  /** Page number from route param, defaults to 1 */
  pageNumber = input(1, { transform: numberAttribute });

  protected loaded = signal(false);
  protected notFound = signal(false);

  protected response = toSignal(
    combineLatest([toObservable(this.section), toObservable(this.pageNumber)]).pipe(
      tap(() => {
        this.loaded.set(false);
        this.notFound.set(false);
      }),
      switchMap(([section, pageNumber]) => {
        const fetch = section === 'blog'
          ? this.#service.blogListing(pageNumber)
          : this.#service.newsListing(pageNumber);

        return fetch.pipe(
          tap((res) => {
            const meta = res.page?.meta;
            if (meta) {
              if (meta.description) this.#meta.updateTag({ content: meta.description, name: 'description' });
              if (meta.og_description) this.#meta.updateTag({ content: meta.og_description, property: 'og:description' });
              if (meta.og_title) this.#meta.updateTag({ content: meta.og_title, property: 'og:title' });
              if (meta.og_image) this.#meta.updateTag({ content: meta.og_image, property: 'og:image' });
            }
          }),
          handleSsrPageErrors({
            onNotFound: () => this.notFound.set(true),
            onSettled: () => this.loaded.set(true),
          }),
        );
      }),
    ),
  );

  protected posts = computed(() => this.response()?.data ?? []);
  protected parentPage = computed(() => this.response()?.page);
  protected pagination = computed(() => this.response()?.pagination);

  protected pages = computed(() => {
    const p = this.pagination();
    if (!p || p.last_page <= 1) return [];
    return Array.from({ length: p.last_page }, (_, i) => i + 1);
  });

  protected pageLink(page: number): string[] {
    const section = this.section();
    if (page === 1) return ['/', section];
    return ['/', section, 'page', String(page)];
  }
}
